/**
 * The brand word in running text (owner rule 2026-10-23).
 *
 * Plain text (emails, store listings, notifications, titles, meta tags, alt text) writes the brand
 * as "MUSIC24". Where a web page can style part of a text (the website, the web app, the SmartLink
 * pages), each "MUSIC24" draws like the logo: "music" in the brand word font and a small "24" in the
 * brand figure font (`assets/music24-brand/fonts`). Those components call `splitBrandWord` and draw
 * each `BRAND_WORD` part themselves; the text the page holds is "music24", so search, copy and screen
 * readers read the normal word.
 *
 * Pure: no React, no react-intl (fym-shared rule).
 */

/** How the brand is written in plain text. */
export const BRAND_TEXT = 'MUSIC24';

/** The two parts that a styled brand word draws: the word and the figures. */
export const BRAND_WORD = { word: 'music', number: '24' } as const;

/** A piece of a text: plain text, or the brand word. */
export type BrandWordPart = { kind: 'text'; text: string } | { kind: 'brand' };

/** True when the text holds the brand word at least once. */
export function hasBrandWord(text: string): boolean {
  return text.includes(BRAND_TEXT);
}

/**
 * Splits a text at each "MUSIC24". Empty text pieces are left out, so "MUSIC24" alone is one
 * brand part. Only the capital form matches: a domain ("music24.com") or a code name stays text.
 */
export function splitBrandWord(text: string): BrandWordPart[] {
  // The capture group keeps each "MUSIC24" as its own piece; empty pieces fall out.
  return text
    .split(/(MUSIC24)/)
    .filter((piece) => piece !== '')
    .map((piece): BrandWordPart => (piece === BRAND_TEXT ? { kind: 'brand' } : { kind: 'text', text: piece }));
}
